import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import SockJS from "sockjs-client";
import { over } from "stompjs";
import Cookies from "js-cookie";
import { API_DETAILS, COOKIE_KEYS } from "../App";

const ChatContext = createContext();
const useChatContext = () => useContext(ChatContext);

const ChatProvider = ({ children }) => {
  // Socket States
  const [stompObject, setStompObject] = useState(null);
  const alreadyRendered = useRef(false);

  // The Stomp client exposes `connected` as a plain mutable property, so it flips
  // without React ever re-rendering. Consumers that watched it could miss the
  // transition entirely and sit on a loading spinner forever. Mirror it as real
  // state here so a connection change actually propagates.
  // "idle" | "connecting" | "connected" | "reconnecting" | "failed"
  const [connectionStatus, setConnectionStatus] = useState("idle");

  // The legacy `stompjs` client has no reconnection of its own — the mobile app's
  // @stomp/stompjs does, via reconnectDelay. A closed SockJS socket cannot be
  // reused, so retrying means building a fresh client; setting it as state re-runs
  // the connect effect below.
  const RECONNECT_BASE_MS = 2000;
  const RECONNECT_MAX_MS = 30000;
  const MAX_RECONNECT_ATTEMPTS = 6;

  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef(null);
  // Set when the socket is being torn down deliberately (sign-out, unmount), so
  // an expected close is not mistaken for a dropped connection worth retrying.
  const closingDeliberately = useRef(false);
  // A rejected token produces two callbacks in sequence: the STOMP ERROR frame,
  // then a plain "Lost connection" string as the socket closes. Without this the
  // second one reads as an ordinary drop and starts retrying anyway.
  const authRejected = useRef(false);

  // Login details
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [chatUserDetails, setChatUserDetails] = useState();
  const [totalMsgCount, setTotalMsgCount] = useState(0);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatData, setChatData] = useState([]);
  const [newMsgUser, setNewMsgUser] = useState([null, null]);

  const selectedChatRef = useRef(null);

  const updateCount = (val) => {
    setTotalMsgCount(val);
  };

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  // Connect using Stomp
  useEffect(() => {
    if (!stompObject) return;

    console.log("Trying......... to connect ");
    setConnectionStatus("connecting");

    // The STOMP CONNECT frame must carry the JWT. StompAuthChannelInterceptor on
    // the backend reads it from this Authorization native header (falling back to
    // an access_token query param) and refuses the connection outright when it is
    // missing, so connecting with empty headers failed for every user with
    // "Unauthenticated STOMP CONNECT". The axios interceptor does not apply here —
    // this is a WebSocket frame, not an HTTP request.
    const token = Cookies.get(COOKIE_KEYS.token);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    stompObject.connect(headers, onConnected, onError);
  }, [stompObject]);

  useEffect(() => {
    if (!isLoggedIn) return;

    closingDeliberately.current = false;
    authRejected.current = false;
    reconnectAttempts.current = 0;
    createStompObject();
  }, [isLoggedIn]);

  // Stop retrying once the provider goes away, otherwise a pending timer would
  // rebuild a socket for a session that no longer exists.
  useEffect(() => {
    return () => {
      closingDeliberately.current = true;
      clearTimeout(reconnectTimer.current);
    };
  }, []);

  useEffect(() => {
    const uid = Cookies.get(COOKIE_KEYS.userId);

    if (uid) setIsLoggedIn(true);
  }, []);

  const createStompObject = () => {
    const url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + '/ws'
    let Sock = new SockJS(url);
    const stompClient = over(Sock);
    setStompObject(stompClient);
  };

  const updateChatUserDetails = (details) => setChatUserDetails({ ...details });

  //   const connectStomp = () => {
  //     if (!stompObject) return;
  //     console.log("Trying to connect...");
  //     stompObject.connect({}, onConnected, onError);
  //   };

  const onConnected = () => {
    // Recorded before the guard below: the socket is genuinely connected at this
    // point, and consumers must learn that even if the guard bails out.
    setConnectionStatus("connected");
    reconnectAttempts.current = 0;

    if (!stompObject) return;

    console.log("Stomp ::: ", stompObject);

    stompObject.subscribe(
      "/user/" + Cookies.get(COOKIE_KEYS.userId) + "/private",
      function (payload) {
        console.log("Chat user details before private ::: ", selectedChat);
        onPrivateMessage(payload);
      }
    );

    //subscribe to get chat History
    listenToChatHistory();
    fetchChatHistory();
  };

  // stompjs routes both a failed handshake and a dropped socket here, so this
  // also fires if the connection is lost after having been established.
  const onError = (err) => {
    console.error("WebSocket error:", err);

    if (closingDeliberately.current || authRejected.current) return;

    // A rejected token will be rejected again on every retry, so retrying just
    // loops until the attempt cap. The session has to be renewed instead.
    const message =
      (err && err.headers && err.headers.message) ||
      (typeof err === "string" ? err : "");
    if (message.includes("Unauthenticated")) {
      console.error("Chat rejected the session token; not retrying.");
      authRejected.current = true;
      setConnectionStatus("failed");
      return;
    }

    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
      setConnectionStatus("failed");
      return;
    }

    // Exponential backoff, capped — a backend restart should not be met with a
    // tight reconnect loop from every open tab.
    const delay = Math.min(
      RECONNECT_BASE_MS * 2 ** reconnectAttempts.current,
      RECONNECT_MAX_MS
    );
    reconnectAttempts.current += 1;
    setConnectionStatus("reconnecting");
    console.log(
      `Chat reconnecting in ${delay}ms (attempt ${reconnectAttempts.current}/${MAX_RECONNECT_ATTEMPTS})`
    );

    clearTimeout(reconnectTimer.current);
    reconnectTimer.current = setTimeout(() => {
      if (closingDeliberately.current) return;
      createStompObject();
    }, delay);
  };

  const onPrivateMessage = (payload) => {
    var payloadData = JSON.parse(payload.body);

    console.log(
      "Private message conds ::: ",
      selectedChatRef.current,
      payloadData.fromId
    );

    if (selectedChatRef.current != payloadData.fromId) {
      setChatUserDetails((prev) => ({
        ...prev,
        [payloadData.fromId]: {
          ...prev[payloadData.fromId],
          count: prev[payloadData.fromId] ? prev[payloadData.fromId].count + 1 : 1,
          message: payloadData.message,
        },
      }));
      setTotalMsgCount((prev) => prev + 1);
    } else {
      const receivedChat = {
        time: payloadData.time,
        message: payloadData.message,
        toId: payloadData.toId,
        fromId: payloadData.fromId,
        seen: payloadData.seen,
      };

      setChatUserDetails((prev) => ({
        ...prev,
        [payloadData.fromId]: {
          ...prev[payloadData.fromId],
          message: payloadData.message,
        },
      }));

      setChatData((prevChatData) => [receivedChat, ...prevChatData]);
    }
    setNewMsgUser([payloadData.fromId, payloadData.toId]);
  };

  const userJoin = () => {
    var chatMessage = {
      senderName: Cookies.get(COOKIE_KEYS.userId),
      status: "JOIN",
    };
    stompObject.send("/app/message", {}, JSON.stringify(chatMessage));
  };

  const fetchChatHistory = () => {
    stompObject.send(`/app/getChatHistory/${Cookies.get(COOKIE_KEYS.userId)}`);
  };

  const listenToChatHistory = () => {
    stompObject.subscribe(
      `/user/${Cookies.get(COOKIE_KEYS.userId)}/chat-history`,
      onReceivedChatHistory
    );
  };

  const onReceivedChatHistory = async (payload, reload = true) => {
    // if (reload) setActiveLoader(true);
    var res = JSON.parse(payload.body);
    console.log("received chat history :: ", res);

    // setChatHistory(res);
    const chatDet = {};
    let totalCnt = 0;
    res.forEach((x) => {
      chatDet[x.uId] = x;
      totalCnt += x.count;
    });
    console.log("Chat Det ::: ", chatDet);
    setChatUserDetails({ ...chatDet });
    setTotalMsgCount(totalCnt);
  };

  return (
    <ChatContext.Provider
      value={{
        isLoggedIn,
        setIsLoggedIn,
        stompObject,
        setStompObject,
        connectionStatus,
        selectedChatRef,
        chatUserDetails,
        updateChatUserDetails,
        updateCount,
        totalMsgCount,
        selectedChat,
        setSelectedChat,
        chatData,
        setChatData,
        newMsgUser
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export { useChatContext, ChatProvider };
